<template>
  <div
    class="twitter-window uk-flex uk-flex-column uk-padding-small uk-background-default uk-border-rounded"
  >
    <div class="uk-flex uk-flex-right">
      <TwitterLoginButton
        :loading="sendingTweet"
        :error="sendTweetError"
        :disabled="!imgResult || !isUserLoggedIn || !!tweetId"
        class="uk-margin-large-bottom"
        @click="sendTweet"
      />
    </div>
    <div>
      <ResourceLoadStatus
        v-if="loadingTwitterLoginUrl"
        class="uk-background-default uk-padding"
        :loading="!loadingTwitterLoginUrl"
        loadingMessage="Connecting with Twitter..."
      >
      </ResourceLoadStatus>
      <div
        v-else-if="!OAuthLoginUrl && !isUserLoggedIn"
        uk-alert
        class="uk-alert-danger uk-border-rounded uk-padding uk-shadow-large uk-flex uk-flex-middle"
      >
        <span uk-icon="icon: warning; ratio: 2" class="uk-margin-small-right"></span>
        Unable to Connect with Twitter
      </div>
      <div
        v-if="loginError"
        uk-alert
        class="uk-alert-danger uk-border-rounded uk-padding uk-shadow-large uk-flex uk-flex-middle"
      >
        <span uk-icon="icon: warning; ratio: 2" class="uk-margin-small-right"></span>
        <div v-if="callbackError">
          <p style="color: red">Callback url without query params</p>
        </div>
        <div v-else>
          <p class="uk-text-large" style="color: red">Error on login</p>
          <p>{{ errorMessage }}</p>
        </div>
        <h2>Please contact your administrator.</h2>
      </div>
    </div>
    <div
      v-if="saveResult"
      class="uk-flex uk-flex-column uk-flex-center uk-background-secondary uk-text-center uk-padding-small uk-border-rounded uk-margin-large-bottom uk-light"
    >
      <h3>Tweet Created Successfully!</h3>
      <div class="uk-flex uk-flex-center">
        <button class="uk-button uk-button-default uk-text-capitalize" @click="openTweet">
          View Tweet
        </button>
        <button
          class="uk-button uk-button-default uk-margin-large-left uk-text-capitalize"
          @click="closePopup"
        >
          Close Popup
        </button>
      </div>
    </div>
    <div class="uk-flex uk-flex-top">
      <div
        class="tweet-icon uk-background-muted uk-padding-small uk-flex uk-flex-center uk-flex-middle uk-border-circle uk-text-muted"
      >
        <span uk-icon="icon: twitter; ratio: 2"></span>
      </div>
      <div class="tweet-area uk-margin-left uk-flex uk-flex-column">
        <TipTap v-model="tweetMessageForDisplay" disabled> </TipTap>
        <img v-if="imgResult" :src="imagePath" />
        <ResourceLoadStatus v-else :loading="loadingImage" loadingMessage="Loading image...">
        </ResourceLoadStatus>
      </div>
    </div>
    <ResourceLoadStatus :loading="loadingAccessToken"> </ResourceLoadStatus>
  </div>
</template>

<script>
import TwitterLoginButton from './TwitterLoginButton.vue';
import ResourceLoadStatus from '@/components/general/ResourceLoadStatus';
import api from './services/index';
import { saveAuthDataToLocalStorage, getAuthDataFromLocalStorage } from './utils';
import { twitterOAuth1Service } from './services/index';
import { mapActions, mapState } from 'vuex';
import { queryToJSON } from '@/assets/js/utils';
import TipTap from '@/components/annotations/TipTap.vue';

export default {
  components: {
    ResourceLoadStatus,
    TwitterLoginButton,
    TipTap,
  },
  async mounted() {
    const twitterCallbackURL = window.location.href;
    const matchStr1 = '^http[s]?://[a-z0-9.:]+/[?]oauth_token=';
    const matchStr2 = '#/social/twitter/login$';
    const isTwitterCallback =
      twitterCallbackURL.match(matchStr1) && twitterCallbackURL.match(matchStr2);

    if (isTwitterCallback) {
      const queryMatchStr = '[?][-&=_a-zA-Z0-9]+(?=#)';
      const query = twitterCallbackURL.match(queryMatchStr)[0];
      const { oauth_token, oauth_verifier } = queryToJSON(query);
      if (!oauth_token || !oauth_verifier) return this.showLoginError();

      await this.generateTokenAndLogin(oauth_token, oauth_verifier);
    } else {
      let { accessToken, userData } = getAuthDataFromLocalStorage();
      this.accessToken = accessToken;
      this.twitterUser = userData;

      if (!this.isUserLoggedIn) {
        await this.requestTwitterLoginUrl();
        this.openTwitterLogin();
      }
    }
    if (this.isUserLoggedIn) {
      this.loadingImage = true;
      this.imgResult = await this.generateSocialPostImage();
      this.loadingImage = false;
    }
  },

  data() {
    return {
      scope: ['tweet.read', 'tweet.write', 'users.read', 'offline.access'],
      accessToken: '',
      twitterUser: null,
      imgResult: null,
      loadingImage: null,
      OAuthLoginUrl: null,
      loadingTwitterLoginUrl: false,
      errorLoadingTwitterLoginUrl: false,
      loadingAccessToken: false,
      callbackError: false,
      loginError: false,
      errorMessage: '',
      sendingTweet: null,
      sendTweetError: null,
      saveResult: null,
      tweetId: null,
    };
  },

  methods: {
    ...mapActions(['generateSocialPostImage', 'saveSocialShare']),

    async requestTwitterLoginUrl() {
      if (this.loadingTwitterLoginUrl) return;
      this.loadingTwitterLoginUrl = true;
      try {
        const response = await api.twitter.requestTwitterLoginUrl(this.scope);
        this.OAuthLoginUrl = response.url;
        this.errorLoadingTwitterLoginUrl = false;
      } catch (error) {
        this.errorLoadingTwitterLoginUrl = true;
        this.errorMessage = error.message;
        console.error('Error requesting Twitter OAuth link', error);
      }
      this.loadingTwitterLoginUrl = false;
    },

    openTwitterLogin() {
      if (!this.OAuthLoginUrl) return;
      try {
        window.location = this.OAuthLoginUrl;
      } catch (e) {
        return Promise.reject(new Error('Error opening OAuth login url'));
      }
    },

    async generateTokenAndLogin(oauth_token, oauth_verifier) {
      if (this.loadingAccessToken) return;
      this.loadingAccessToken = true;

      try {
        // Request accessToken exchange to the backend service
        const response = await twitterOAuth1Service.generateAccessTokenAndLogin(
          oauth_token,
          oauth_verifier
        );

        saveAuthDataToLocalStorage(response.accessToken, response.user);
        this.accessToken = response.accessToken;
        this.twitterUser = response.user;
      } catch (error) {
        console.error('Error on login', error);
        this.loginError = true;
        this.errorMessage = error.message;
      }
      this.loadingAccessToken = false;
    },

    async sendTweet() {
      if (this.sendingTweet) return;
      this.sendingTweet = true;

      try {
        const response = await api.twitter.createTweet(
          this.accessToken,
          this.tweetMessage,
          this.imagePath
        );
        this.saveResult = response?.tweetId;

        if (response?.tweetId) {
          this.tweetId = response.tweetId;
          try {
            //
            // save tweet
            if (this.visitor?._id) {
              await this.saveSocialShare({
                publicLinkID: this.publicLinkData?._id,
                network: this.publicTargetNetwork,
                annotationId: this.publicTargetAnnotationId,
              });
            }
          } catch (error) {
            console.error('Error saving tweet to DB', error);
          }
        }
      } catch (error) {
        this.sendTweetError = true;
        console.error('Error creating tweet', error);
      }
      this.sendingTweet = false;
    },

    openTweet() {
      window.location = `https://www.twitter.com/`;
    },

    showLoginError() {
      this.loginError = true;
      this.callbackError = true;
    },

    closePopup() {
      window.close();
    },
  },

  computed: {
    ...mapState([
      'visitor',
      'publicTargetURL',
      'publicPostMessage',
      'publicLinkData',
      'publicTargetNetwork',
      'publicTargetAnnotationId',
    ]),

    tweetMessage() {
      return `${this.publicPostMessage} ${this.publicTargetURL}`;
    },

    tweetMessageForDisplay() {
      return `${this.publicPostMessage} <a href="${this.publicTargetURL}">${this.publicTargetURL}</a>`;
    },

    imagePath() {
      return (
        'https://noetic1storage.blob.core.windows.net/' +
        'objectively-social-images/' +
        this.imgResult
      );
    },

    isUserLoggedIn() {
      return !!this.accessToken;
    },

    scopeAsString() {
      return this.scope.join(',');
    },
  },
};
</script>

<style lang="sass" scoped>
.tweet
  &-area
    min-width: calc(90vw - 4rem)
    line-height: 1.4
    word-break: break-all
  &-icon
    min-width: 3rem
    min-height: 3rem
</style>
